import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, MessageCircle, FileText } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { translations } from "@/lib/translations";
import { SupportSEO } from "@/components/support/SupportSEO";

const Support = () => {
  const { language, getLocalizedPath } = useLanguage();
  const t = translations[language].support;

  return (
    <Layout>
      <SupportSEO />
      <main className="pt-28 pb-16 sm:pt-32 sm:pb-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <article className="max-w-3xl mx-auto">
            <header className="text-center mb-12">
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
                {t.title}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t.subtitle}
              </p>
            </header>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4">
                    <Mail className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-lg text-card-foreground mb-2">
                    {t.emailSupport.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t.emailSupport.description}
                  </p>
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <a href={`mailto:${t.emailSupport.email}`}>
                      {t.emailSupport.email}
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-card border-border hover:border-primary/50 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4">
                    <MessageCircle className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-lg text-card-foreground mb-2">
                    {t.feedback.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t.feedback.description}
                  </p>
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <a href={`mailto:${t.emailSupport.email}`}>
                      {t.feedback.buttonText}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* FAQ Section */}
            <section aria-labelledby="faq-heading" className="mb-12">
              <h2 id="faq-heading" className="font-heading text-2xl font-bold text-foreground mb-6 text-center">
                {t.faq.title}
              </h2>

              <div className="space-y-4">
                {t.faq.questions.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-card-foreground mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {faq.answer}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Legal links */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-muted-foreground mb-4">
                <FileText className="w-5 h-5" />
                <span>{t.legalDocs.title}</span>
              </div>
              <div className="flex justify-center gap-4">
                <Button asChild variant="ghost" size="sm">
                  <a href={getLocalizedPath("privacy")}>{t.legalDocs.privacyLink}</a>
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <a href={getLocalizedPath("terms")}>{t.legalDocs.termsLink}</a>
                </Button>
              </div>
            </div>
          </article>
        </div>
      </main>
    </Layout>
  );
};

export default Support;
